import { Fragment } from 'react';
import type { ConversationDetail, MessageItem } from '@/shared/api';
import type { ContextData } from '../context-panel/ThreadContextPanel';
import { useThreadContext } from '../context-panel/useThreadContext';
import { cameFromChannel, channelLabel, dayLabel, messageTime } from '../inboxModel';
import { threadDisplayName } from '../threadDisplayName';
import { ConversationActions } from './ConversationActions';
import { ConversationChips } from './ConversationChips';
import { ConversationComposer } from './ConversationComposer';
import { ConversationHeader } from './ConversationHeader';
import { MessageBubble } from './MessageBubble';
import { MessageListState, type MessageListStatus } from './MessageListState';
import { useStickToNewest } from './useStickToNewest';

/**
 * Mittlere Spalte: Kopf mit Kontext-Band, Verlauf mit Tagestrennern, Schreibfeld. Präsentational —
 * Daten und Senden kommen von oben; eine Fassung für die geteilte Ansicht und den Direktlink.
 */
export function ConversationPane({
  thread,
  messages,
  messagesStatus,
  onSend,
  readOnly,
  demoContext,
  onClose,
  onNoReplyNeeded,
  onMarkRead,
  onBack,
  onOpenContext,
}: {
  thread: ConversationDetail;
  messages: MessageItem[];
  messagesStatus?: MessageListStatus;
  onSend: (text: string) => Promise<MessageItem>;
  readOnly?: boolean;
  demoContext?: ContextData;
  onClose?: () => void;
  onNoReplyNeeded?: () => void;
  onMarkRead?: () => void;
  /** Gesetzt, wenn die Unterhaltung am Handy die einzige Ebene ist — Rückweg zur Liste. */
  onBack?: () => void;
  /** Gesetzt, solange der Kontext keine eigene Spalte hat: öffnet ihn als Blatt. */
  onOpenContext?: () => void;
}) {
  const verlauf = useStickToNewest(thread.id, messages.length);
  const facts = useThreadContext({
    threadId: thread.id,
    bookingId: demoContext ? null : thread.bookingId,
    propertyId: demoContext ? null : thread.propertyId,
    otaReservationId: demoContext ? null : thread.otaReservationId,
    canViewPii: false,
  });
  const booking = demoContext ? demoContext.booking : (facts.booking.data ?? null);
  const property = demoContext ? demoContext.property : (facts.property.data ?? null);
  const name = threadDisplayName(thread.guestName, thread.subject) ?? 'Gast';
  const now = new Date();
  const hasActions = !readOnly && Boolean(onNoReplyNeeded || onClose || onMarkRead);
  const lastHost = [...messages].reverse().find((m) => m.sender === 'Host');
  const answeredVia = lastHost && cameFromChannel(lastHost) ? channelLabel(thread.channel) : null;

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col">
      <ConversationHeader
        thread={thread}
        name={name}
        answeredVia={answeredVia}
        onBack={onBack}
        onOpenContext={onOpenContext}
        actions={
          hasActions ? (
            <ConversationActions
              thread={thread}
              onClose={onClose}
              onNoReplyNeeded={onNoReplyNeeded}
              onMarkRead={onMarkRead}
            />
          ) : undefined
        }
        chips={
          <ConversationChips
            thread={thread}
            booking={booking}
            propertyName={property?.adminDisplayName ?? property?.name ?? thread.propertyName}
            live={!readOnly}
          />
        }
      />
      {/* `log`: neue Einträge werden angesagt, ohne dass der Leser die Stelle verliert. */}
      <div
        ref={verlauf.ref}
        onScroll={verlauf.onScroll}
        role="log"
        aria-label="Nachrichtenverlauf"
        className="bg-paper/60 flex min-h-0 min-w-0 flex-1 flex-col gap-3 overflow-y-auto overflow-x-hidden px-3 py-4 sm:px-5"
      >
        <div ref={verlauf.contentRef} className="flex min-w-0 flex-col gap-3">
          {messages.map((m, index) => {
            const day = dayLabel(messageTime(m), now);
            const showDay = index === 0 || dayLabel(messageTime(messages[index - 1]!), now) !== day;
            return (
              <Fragment key={m.id}>
                {showDay && (
                  <span className="text-ink-faint self-center py-1 font-mono text-[11px]">
                    {day}
                  </span>
                )}
                <MessageBubble m={m} conversationId={thread.id} channel={thread.channel} />
              </Fragment>
            );
          })}
          <MessageListState status={messagesStatus} empty={messages.length === 0} />
        </div>
      </div>
      <ConversationComposer
        key={thread.id}
        name={name}
        channel={thread.channel}
        closed={thread.isClosed}
        readOnly={readOnly}
        onSend={(text) => {
          verlauf.stickNow();
          return onSend(text);
        }}
      />
    </div>
  );
}
